import { defineStore } from 'pinia';
import { readCookie } from '@/modules/cookies';
import { generateUUID } from '@/modules/uuid';
import { logger } from '@/logging/logger';
import { LogCategory } from '@/logging/types';
import { useGeoStore } from '@/stores/geo';
import { usePreferenceStore } from '@/stores/preference';

export interface EncounterState {
  start: string | null;
  uuid: string | null;
  patient_uuid: string | null;
  pausedDurationMs: number;
  engagementKey: string | null;
}

// snake_case is load bearing: savedEncounters clones this object and resumes it back
export const defaultState = (): EncounterState => ({
  start: null,
  uuid: null,
  patient_uuid: null,
  pausedDurationMs: 0,
  engagementKey: null,
});

export const useEncounterStore = defineStore('encounter', {
  persist: true,
  state: defaultState,

  actions: {
    async reset(): Promise<void> {
      logger.info('auth', 'session.reset', undefined, 'Session reset initiated').then();
      const engagementKey = this.engagementKey;
      this.$reset();
      this.$patch({ engagementKey });
    },

    resume(payload: Partial<EncounterState>): void {
      this.$patch(payload);

      // Key names only: the values are the encounter and patient identifiers.
      logger.info(
        LogCategory.ENCOUNTER,
        'encounter.resume',
        { fields: Object.keys(payload ?? {}) },
        'Encounter resumed',
      ).then();
    },

    // named begin, not start: an action would shadow the state field of the same name.
    // identity is assigned before the first await, so callers can read uuid without awaiting
    async begin(): Promise<void> {
      this.$patch({
        start: new Date().toISOString(),
        uuid: generateUUID(),
        patient_uuid: `ThinkMD-${generateUUID()}`,
      });

      const geoPermission = usePreferenceStore().getResponse('geo_permission', 'granted');

      logger.info(
        LogCategory.ENCOUNTER,
        'encounter.begin',
        { geoRequested: Boolean(geoPermission.selected) },
        'Encounter started',
      ).then();

      if (geoPermission.selected) {
        await useGeoStore().getPosition();
      }
    },

    // vuex bucketed this per session cookie; pinia persists under one key
    syncEngagement(): void {
      const key = readCookie('session');
      if (this.engagementKey === key) return;

      const hadPrevious = this.engagementKey !== null;

      this.$reset();
      this.$patch({ engagementKey: key });

      // hadPrevious only: engagementKey is the session cookie value, so it is a credential.
      logger.info(
        LogCategory.ENCOUNTER,
        'encounter.engagement.changed',
        { hadPrevious },
        'Engagement changed, encounter state reset',
      ).then();
    },
  },
})
